<template>
  <div class="h-full">
    <a-dropdown :trigger="['click']">
      <a
        class="h-full cursor-pointer"
        @click.prevent
      >
        <span class="inline-flex size-12 items-center justify-center rounded-full bg-slate-200 text-sm text-white">
          <img
            referrerpolicy="no-referrer"
            crossorigin="anonymous"
            class="w-full rounded-full border-none align-middle shadow-lg"
            :src="user.photoURL"
            alt="Profile Picture"
            width="96"
            height="96"
          >
        </span>
      </a>
      <template #overlay>
        <a-menu class="rounded-lg">
          <a-menu-item
            key="0"
            class="px-8 py-2"
          >
            <router-link
              to="/app/settings"
              class="font-semibold"
            >
              <i class="fa-solid fa-cog fa-fw" />&nbsp;
              Account Settings
            </router-link>
            <p class="mb-1 pl-6 text-xs">
              {{ user.email ? user.email : user.displayName }}
            </p>
          </a-menu-item>
          <a-menu-item
            v-if="userMode == 'user'"
            key="1"
            class="px-8 py-2"
          >
            <router-link
              to="/app/settings/billing"
              class="font-semibold"
            >
              <i class="fa-solid fa-credit-card fa-fw" />&nbsp;
              Billing Portal
            </router-link>
          </a-menu-item>
          <a-menu-item
            key="3"
            class="px-8 py-2"
            @click="setDarkMode(!darkMode)"
          >
            <span class="font-semibold">
              <i
                class="fa-solid fa-fw"
                :class="darkMode ? 'fa-sun' : 'fa-moon'"
              />&nbsp;
              {{ darkMode ? 'Light' : 'Dark' }} Mode
            </span>
          </a-menu-item>
          <a-menu-divider />
          <a-menu-item
            key="4"
            class="px-8 py-2"
            @click="logout"
          >
            <span class="font-semibold">
              <i class="fa-solid fa-sign-out fa-fw" />&nbsp;
              Logout
            </span>
          </a-menu-item>
        </a-menu>
      </template>
    </a-dropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useStore } from '../../api/index.ts';
import { message } from 'ant-design-vue';
import { setHTMLDarkMode } from '../../assets/js/helpers/DarkThemeHelper.ts';

const store = useStore();

const user = {
  email: store.user?.email ?? store.user?.providerData?.find(p => p?.email)?.email ?? '',
  displayName: store.user?.displayName || '',
  photoURL: store.user?.photoURL || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp',
};

const userMode = computed(() => store.userMode);

const darkMode = computed(() => store.userPreferences.darkMode);

onMounted(async () => {
  if (store.user?.photoURL == null) {
    // If we have crypto support, use it, otherwise serve default image from CDN
    if (window.crypto) {
      const hash = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(store.user?.email ?? store.user?.providerData?.find(p => p?.email)?.email ?? ''));
      const hex = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
      user.photoURL ='https://www.gravatar.com/avatar/' + hex + '?d=mp';
    }
  }
});

async function logout() {
  try {
    if (await store.isSSOUser()) {
      await store.initiateSSOLogout();
      return;
    }
    await store.logout();
    window.location.href = '/auth/login';
  }
  catch (err: any) {
    console.warn(err.message);
  }
}
function setDarkMode(darkMode: boolean) {
  store.changeDarkMode(darkMode).then(() => {
    setHTMLDarkMode(darkMode);
    message.success('Dark mode updated.');
    store.userPreferences.darkMode = darkMode;
  }).catch((err: any) => {
    // Still update local state, but show error message
    console.error(err);
    setHTMLDarkMode(darkMode);
    store.userPreferences.darkMode = darkMode;
    message.error('Error saving preferences. Please try again later.');
  });
}
</script>
